import React from 'react';
import { BookingStep } from '../store/ServiceBookingFlowStore';

// Import step components
import {
  Step1UserSelection, 
  Step2ExistingPatient,
  Step7TimeSelection,
  Step8Login,
  Step9FamilyMember,
  Step10AddressMedicare,
  Step11ServiceLogin,
} from '../components/BookingSteps';
import ServiceConformationStep from '../components/ServiceConfirmationStep';

// TypeScript interface for useStepRenderer hook props
interface UseStepRendererProps {
  currentStep: number;
  isForUser: boolean;
  startDate: any; // DateTime type from luxon
  setStartDate: (date: any) => void;
  appointmentSlotsData: any;
  appointmentSlotsLoading: boolean;
  numberOfColumns: number;
  selectedTime: any;
  handleCloseModal: () => void;
  selectMe: () => void;
  handleSelectFamilyMember: (familyMember: any) => void;
  handleOpenCreateFamilyMemberModal: () => void;
  showExistingPatientStep?: boolean; // New prop to control step visibility
}

/**
 * useStepRenderer custom hook
 * Returns a function that renders the appropriate step component based on current step
 */
export const useStepRenderer = (props: UseStepRendererProps) => {
  const {
    currentStep,
    isForUser,
    startDate,
    setStartDate,
    appointmentSlotsData,
    appointmentSlotsLoading,
    numberOfColumns,
    selectedTime,
    handleCloseModal,
    selectMe,
    handleSelectFamilyMember,
    handleOpenCreateFamilyMemberModal,
    showExistingPatientStep = true, // Default to showing the step if not specified
  } = props;

  // Function to render the appropriate component based on current step
  const renderStep = React.useCallback(() => {
    // If we're supposed to be at EXISTING_PATIENT step but that step is hidden,
    // skip ahead to TIME_SELECTION
    if (currentStep === BookingStep.EXISTING_PATIENT && !showExistingPatientStep) {
      return (
        <Step7TimeSelection
          startDate={startDate}
          setStartDate={setStartDate}
          appointmentSlotsData={appointmentSlotsData}
          isLoading={appointmentSlotsLoading}
          numberOfColumns={numberOfColumns}
        />
      );
    }
    
    // Normal step rendering logic with adjustments for when Existing Patient step is hidden
    switch (currentStep) {
      case BookingStep.USER_SELECTION:
        return (
          <Step1UserSelection
            selectMe={selectMe}
            onSelectFamilyMember={handleSelectFamilyMember}
            onOpenCreateFamilyMemberModal={handleOpenCreateFamilyMemberModal}
          />
        );
      case BookingStep.EXISTING_PATIENT:
        // This step is only rendered if showExistingPatientStep is true
        if (showExistingPatientStep) {
          return (<Step2ExistingPatient
          isForFamilyMember={!isForUser}
          />
        );
        }
        // If this step should be skipped, fall through to the next case
        // This is a safeguard, though the logic above should prevent this case
        return (
          <Step7TimeSelection
            startDate={startDate}
            setStartDate={setStartDate}
            appointmentSlotsData={appointmentSlotsData}
            isLoading={appointmentSlotsLoading}
            numberOfColumns={numberOfColumns}
          />
        );
      case BookingStep.TIME_SELECTION:
        return (
           <Step7TimeSelection
              startDate={startDate}
              setStartDate={setStartDate}
              appointmentSlotsData={appointmentSlotsData}
              isLoading={appointmentSlotsLoading}
              numberOfColumns={numberOfColumns}
           />
        );
        //return <Step11ServiceLogin/>;
        // return <Step10AddressMedicare />;
      case BookingStep.LOGIN:
        return <Step8Login />;
      case BookingStep.ADDRESS_MEDICARE:
          return <Step10AddressMedicare />;
      case BookingStep.SERVICE_LOGIN:
          return <Step11ServiceLogin />;
      case BookingStep.FAMILY_MEMBER:
        return (
          <Step9FamilyMember
            onSelectFamilyMember={handleSelectFamilyMember}
            onOpenCreateFamilyMemberModal={handleOpenCreateFamilyMemberModal}
          />
        );
      case BookingStep.CONFIRMATION:
        return (
          <ServiceConformationStep
            selectedTime={selectedTime}
            onClose={handleCloseModal}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  }, [currentStep, showExistingPatientStep, startDate, setStartDate, appointmentSlotsData, appointmentSlotsLoading, numberOfColumns, selectMe, handleSelectFamilyMember, handleOpenCreateFamilyMemberModal, selectedTime, handleCloseModal, isForUser]);

  return renderStep;
};

export default useStepRenderer;